/**
 * A PAGE THAT READS ITSELF AGAIN, GENTLY (provisional P106, ops #81). The owner's first sign-in to
 * production, on 2026-10-19, met the holding screen, "Your home is being set up", which said nothing
 * of whether anything was happening. Ops composes a first fill three rows a run, one run every five
 * minutes, the household among the first (its contract, section 3, "The first fill"), so the
 * holding screen reads itself again until the household comes, and Home does while more of the
 * first fill may still be arriving. Neither waits on ops: each read is the Worker's answer from the
 * copy it holds (isolation rule 5).
 *
 * D1's rows read are the account's, the phone system's among them, so the reads are few and end:
 *
 * - FIFTEEN SECONDS before the first read again, four times, then each wait doubled every four
 *   reads, and never longer than the page's own cap, nor shorter than fifteen seconds;
 * - NOTHING WHILE THE TAB IS HIDDEN. Once it is shown again, a read at once where its wait has
 *   passed since the last read, or the rest of the wait where it has not, so hiding and showing a
 *   tab never reads more often than every fifteen seconds;
 * - A BOUND. Once the page has waited that long in view, it stops by itself and says so (`stopped`),
 *   and "Check again" (`again`) reads at once and starts over from fifteen seconds;
 * - NOTHING once the page no longer asks for it (`on`) or has gone.
 *
 * The read is the page's own `reload` (`components/Guarded.tsx`), which keeps the page as it was
 * when a read does not go through, and goes to the door or the page that says to call where the
 * Worker says so.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** The wait before the first read again, and the shortest wait there is. */
export const FIRST_WAIT_MS = 15_000;

/** How many reads each wait serves before it doubles. */
const READS_A_WAIT = 4;

/** The wait before the next read, once `reads` reads again have been made: see the header. */
export function waitBefore(reads: number, capMs: number): number {
  const doubled = FIRST_WAIT_MS * 2 ** Math.floor(reads / READS_A_WAIT);
  return Math.max(FIRST_WAIT_MS, Math.min(doubled, capMs));
}

export interface ReadAgainOptions {
  /** Whether the page asks to be read again now. */
  readonly on: boolean;
  /** The longest wait between two reads. */
  readonly capMs: number;
  /** How long the page waits in view, all its waits added up, before it stops by itself. */
  readonly boundMs: number;
}

export interface ReadingAgain {
  /** It has stopped by itself, its bound spent, so the page no longer updates by itself. */
  readonly stopped: boolean;
  /** Reads at once and starts over from the first wait: "Check again". */
  readonly again: () => void;
}

/** Reads the page again by `reload`, gently, as the header says. */
export function useReadAgain(
  reload: () => void,
  { on, capMs, boundMs }: ReadAgainOptions,
): ReadingAgain {
  const [stopped, setStopped] = useState(false);
  /** Bumped by "Check again", so the waits start over. */
  const [run, setRun] = useState(0);
  const reloadNow = useRef(reload);
  reloadNow.current = reload;
  /** Reads made again since the page was drawn, or since "Check again". */
  const reads = useRef(0);
  /** Every wait it has served, added up, which the bound is held to. */
  const waited = useRef(0);
  /** When the page was last read: drawn from its own read, then each read again. */
  const lastRead = useRef(Date.now());

  useEffect(() => {
    if (!on || stopped) return;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const read = () => {
      waited.current += waitBefore(reads.current, capMs);
      reads.current += 1;
      lastRead.current = Date.now();
      reloadNow.current();
      if (waited.current >= boundMs) {
        setStopped(true);
        return;
      }
      schedule();
    };

    function schedule() {
      clearTimeout(timer);
      timer = undefined;
      if (document.visibilityState === 'hidden') return;
      const rest = waitBefore(reads.current, capMs) - (Date.now() - lastRead.current);
      if (rest <= 0) read();
      else timer = setTimeout(read, rest);
    }

    document.addEventListener('visibilitychange', schedule);
    schedule();
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', schedule);
    };
  }, [on, stopped, run, capMs, boundMs]);

  const again = useCallback(() => {
    reads.current = 0;
    waited.current = 0;
    lastRead.current = Date.now();
    reloadNow.current();
    setStopped(false);
    setRun((count) => count + 1);
  }, []);

  return { stopped, again };
}
